// components/Enrollment/StudentMyRequests.js
// ─────────────────────────────────────────────────────────────────────────────
// "My Applications" — where a student finds out where their enrolment request
// stands.
//
// The page answers three questions in priority order, and the layout follows
// that order rather than the data shape:
//   1. What happened?      → the status pill and, for a decision, the verdict.
//   2. Why?                → the reason callout. A hold or a rejection without
//                            its reason is the single thing that generates a
//                            support message, so the reason is promoted to a
//                            first-class block, never a grey subtitle.
//   3. What do I do next?  → exactly one primary action per card.
//
// The timeline is collapsed by default: it matters to the student who was put
// on hold and then approved, and is noise for the eight-out-of-ten whose row
// only ever had one entry.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, Clock, CheckCircle2, XCircle, AlertCircle, ClipboardList,
  PauseCircle, ArrowRight, Calendar, RefreshCw, ChevronDown, History,
  Info, GraduationCap, Wallet,
} from 'lucide-react';
import { AppBadge, EmptyState, PageHeader, AppButton } from '../ui';
import { getMyRequests } from '../../utils/enrollmentApi';
import './_enrollment-pages.scss';

/**
 * One entry per status the server can send.
 *
 * `tone` drives the card's accent stripe, the pill and the reason callout from
 * a single token, so a new status cannot end up half-styled.
 */
const STATUS_CFG = {
  pending: {
    tone: 'amber', icon: Clock, label: 'In Review',
    blurb: 'Your application is with the admin team. You will get an email as soon as they decide.',
  },
  on_hold: {
    tone: 'amber', icon: PauseCircle, label: 'On Hold',
    blurb: 'Still open — the team has paused the decision and will follow up.',
  },
  approved: {
    tone: 'emerald', icon: CheckCircle2, label: 'Approved',
    blurb: 'You are enrolled. Head to My Learning to get started.',
  },
  paid: {
    tone: 'emerald', icon: CheckCircle2, label: 'Enrolled',
    blurb: 'Payment received and your seat is confirmed.',
  },
  rejected: {
    tone: 'rose', icon: XCircle, label: 'Declined',
    blurb: 'This application was not approved. You are welcome to apply again.',
  },
  expired: {
    tone: 'slate', icon: AlertCircle, label: 'Expired',
    blurb: 'This application is no longer active.',
  },
};

/** The filter rail. `match` is what each tab counts and shows. */
const TABS = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'active', label: 'In Review', match: (r) => ['pending', 'on_hold'].includes(r.status) },
  { key: 'enrolled', label: 'Enrolled', match: (r) => ['approved', 'paid'].includes(r.status) },
  { key: 'closed', label: 'Closed', match: (r) => ['rejected', 'expired'].includes(r.status) },
];

const fmtDate = (d) => (d
  ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : '');

const fmtDateTime = (d) => (d
  ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '');

/** Card skeleton — mirrors the real card so nothing shifts when data lands. */
function RequestsSkeleton() {
  return (
    <div className="enr-req__list" aria-hidden="true">
      {[0, 1, 2].map(i => (
        <div className="enr-req__card enr-req__card--skel" key={i}>
          <div className="shimmer enr-req__skel-thumb" />
          <div className="enr-req__skel-body">
            <div className="shimmer enr-req__skel-line enr-req__skel-line--title" />
            <div className="shimmer enr-req__skel-line enr-req__skel-line--meta" />
            <div className="shimmer enr-req__skel-line enr-req__skel-line--meta2" />
          </div>
          <div className="shimmer enr-req__skel-pill" />
        </div>
      ))}
    </div>
  );
}

/** The decision trail, collapsed by default. */
function Timeline({ history }) {
  if (!history?.length) return null;
  return (
    <ol className="enr-req__timeline">
      {history.map((h, i) => {
        const cfg = STATUS_CFG[h.status] || STATUS_CFG.pending;
        const Icon = cfg.icon;
        return (
          <li key={i} className={`enr-req__tl-item enr-req__tl-item--${cfg.tone}`}>
            <span className="enr-req__tl-dot"><Icon size={12} /></span>
            <div className="enr-req__tl-body">
              <span className="enr-req__tl-label">{cfg.label}</span>
              <span className="enr-req__tl-time">{fmtDateTime(h.at)}</span>
              {h.reason && <p className="enr-req__tl-reason">{h.reason}</p>}
              {h.byName && <span className="enr-req__tl-by">by {h.byName}</span>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function RequestCard({ req, onNavigate }) {
  const [showTimeline, setShowTimeline] = useState(false);

  const course = req.courseId || {};
  const batch = req.batchId || {};
  const cfg = STATUS_CFG[req.status] || STATUS_CFG.pending;
  const StatusIcon = cfg.icon;
  const courseName = course.name || course.title || 'Course';
  const thumb = course.thumbnailUrl || course.thumbnail || course.image;

  // The reason the student most needs to see, whichever field holds it.
  const reason = req.status === 'rejected' ? req.rejectReason
    : req.status === 'on_hold' ? req.holdReason
      : '';

  // Rows written before statusHistory existed still deserve a timeline.
  const history = req.statusHistory?.length
    ? req.statusHistory
    : [{ status: 'pending', at: req.requestedAt || req.createdAt, reason: '' }];

  const isEnrolled = ['approved', 'paid'].includes(req.status);
  const isClosed = ['rejected', 'expired'].includes(req.status);

  return (
    <article className={`enr-req__card enr-req__card--${cfg.tone}`}>
      <div className="enr-req__main">
        <div className="enr-req__thumb">
          {thumb ? <img src={thumb} alt="" /> : <BookOpen size={22} />}
        </div>

        <div className="enr-req__info">
          <div className="enr-req__title-row">
            <h3 className="enr-req__title">{courseName}</h3>
            {/* AppBadge has no neutral colour token, so the terminal
                "expired" state borrows the secondary variant instead. */}
            <AppBadge
              size="sm"
              className="enr-req__pill"
              {...(cfg.tone === 'slate' ? { variant: 'secondary' } : { color: cfg.tone })}
            >
              <StatusIcon size={13} /> {cfg.label}
            </AppBadge>
          </div>

          <div className="enr-req__meta">
            <span className="enr-req__meta-item">
              <Calendar size={13} />
              Applied {fmtDate(req.requestedAt || req.createdAt)}
            </span>
            {batch.name && (
              <span className="enr-req__meta-item">
                <GraduationCap size={13} /> {batch.name}
              </span>
            )}
            {req.amountPaid > 0 && (
              <span className="enr-req__meta-item">
                <Wallet size={13} /> ₹{Number(req.amountPaid).toLocaleString('en-IN')} paid
              </span>
            )}
            {req.enrollmentMode === 'request' && (
              <span className="enr-req__meta-item enr-req__meta-item--tag">Approval required</span>
            )}
            {req.enrollmentMode === 'qr' && (
              <span className="enr-req__meta-item enr-req__meta-item--tag">Paid by UPI QR — being verified</span>
            )}
          </div>

          <p className="enr-req__blurb">{cfg.blurb}</p>
        </div>
      </div>

      {/* Why — the block that stops this page generating support tickets. */}
      {reason && (
        <div className={`enr-req__reason enr-req__reason--${cfg.tone}`}>
          <Info size={15} />
          <div>
            <span className="enr-req__reason-label">
              {req.status === 'on_hold' ? 'Why it is on hold' : 'Reason'}
            </span>
            <p>{reason}</p>
          </div>
        </div>
      )}

      <footer className="enr-req__actions">
        <button
          type="button"
          className={`enr-req__tl-toggle ${showTimeline ? 'is-open' : ''}`}
          onClick={() => setShowTimeline(v => !v)}
          aria-expanded={showTimeline}
        >
          <History size={14} />
          {showTimeline ? 'Hide' : 'View'} history
          <ChevronDown size={14} className="enr-req__tl-chev" />
        </button>

        <div className="enr-req__action-btns">
          {isEnrolled && (
            <AppButton size="sm" onClick={() => onNavigate('/s/learning')}>
              Go to My Learning <ArrowRight size={14} />
            </AppButton>
          )}
          {isClosed && course._id && (
            <AppButton size="sm" onClick={() => onNavigate(`/s/courses/${course._id}`)}>
              <RefreshCw size={14} /> Apply Again
            </AppButton>
          )}
          {!isEnrolled && !isClosed && course._id && (
            <AppButton size="sm" variant="secondary" onClick={() => onNavigate(`/s/courses/${course._id}`)}>
              View Course
            </AppButton>
          )}
        </div>
      </footer>

      {showTimeline && <Timeline history={history} />}
    </article>
  );
}

export default function StudentMyRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await getMyRequests();
        setRequests(res.data?.data || []);
      } catch {
        setRequests([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const counts = useMemo(() => {
    const out = {};
    TABS.forEach(t => { out[t.key] = requests.filter(t.match).length; });
    return out;
  }, [requests]);

  const visible = useMemo(() => {
    const t = TABS.find(x => x.key === tab) || TABS[0];
    return requests.filter(t.match);
  }, [requests, tab]);

  return (
    <div className="enr-req">
      <PageHeader
        title="My Applications"
        description="Track the status of every course you have applied to"
        icon={ClipboardList}
      />

      {loading ? (
        <RequestsSkeleton />
      ) : requests.length === 0 ? (
        <EmptyState
          icon={<BookOpen size={48} />}
          title="No applications yet"
          description="Browse the course catalogue and apply to join a batch — you can pay online or request approval from the admin team."
          actionLabel="Browse Courses"
          onAction={() => navigate('/s/courses')}
        />
      ) : (
        <>
          {/* Filter rail. Counts live on the tabs rather than in a separate stat
              strip — one row instead of two, which matters on a phone and
              removes the "which number belongs to which filter" guess. */}
          <div className="enr-req__tabs" role="tablist">
            {TABS.map(t => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={`enr-req__tab ${tab === t.key ? 'is-active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                <span className="enr-req__tab-count">{counts[t.key] || 0}</span>
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <EmptyState
              icon={<ClipboardList size={44} />}
              title="Nothing here"
              description="No applications match this filter."
              actionLabel="Show all"
              onAction={() => setTab('all')}
            />
          ) : (
            <div className="enr-req__list">
              {visible.map(req => (
                <RequestCard key={req._id} req={req} onNavigate={navigate} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
